import "antd/dist/antd.css";
import "../App.css";

import GuideArticle from "../components/GuideArticle";
import GuideSection from "../components/GuideSection";
import GuideStackLead from "../components/GuideStackLead";
import GuideThreeJobs from "../components/GuideThreeJobs";
import GuideCtaBand from "../components/GuideCtaBand";
import CtaCard from "../components/CtaCard";
import RelatedGuides from "../components/RelatedGuides";
import usePageMeta from "../usePageMeta";
import { GUIDES } from "../data/guides";

const META = GUIDES.find((g) => g.slug === "motorbike-navigation-tools")!;

// Category accents pulled from the brand --gt-feature-* palette rather
// than the old bright blues/oranges — see the design handoff's category
// colour mapping.
const CATEGORY_ACCENT: Record<string, { fill: string; chipBg: string; chipText: string }> = {
    twisty: { fill: "#c1654a", chipBg: "rgba(193,101,74,.13)", chipText: "#a1462c" },
    general: { fill: "#5a6772", chipBg: "rgba(90,103,114,.13)", chipText: "#485460" },
    gps: { fill: "#0f4c5c", chipBg: "rgba(15,76,92,.12)", chipText: "#0f4c5c" },
    mirror: { fill: "#5b8a72", chipBg: "rgba(91,138,114,.14)", chipText: "#3f6b53" },
};

type Tool = {
    name: string;
    tagline: string;
    cost: string;
    strengths: string;
    weaknesses: string;
    bestFor: string;
    url?: string;
};

type Category = {
    id: keyof typeof CATEGORY_ACCENT;
    heading: string;
    eyebrow: string;
    intro: string;
    tools: Tool[];
};

const CATEGORIES: Category[] = [
    {
        id: "twisty",
        heading: "Twisty-road planners",
        eyebrow: "Made for riders",
        intro:
            "The apps built specifically to bias routes toward corners rather than the fastest line. Small user bases but strong opinions. This is where you plan a Sunday ride, not the airport run.",
        tools: [
            {
                name: "Calimoto",
                tagline: "Best-in-class for planning a detailed route.",
                cost: "Free trial · Premium ≈ £40/year",
                url: "https://calimoto.com",
                strengths:
                    "Route shaping is the killer feature — grab the route line anywhere and drag it to reshape it, snapping to real roads as you go. Force the route down that fun back road even when the algorithm wouldn't pick it, without turning the plan into a chain of thirty waypoints. Curviness slider on top of that. Community-shared tours across Europe. Tour recording with the video-highlights feature.",
                weaknesses:
                    "The subscription stings if you only ride occasionally. The router is opinionated in a way that occasionally sends you 20 minutes around a good road just to catch one corner. Voice guidance can get quirky in strong accents.",
                bestFor:
                    "Riders who plan the route meticulously and want specific roads in the plan — the route shaping is what wins the subscription argument.",
            },
            {
                name: "Kurviger",
                tagline: "The cult favourite among planners.",
                cost: "Web planner free · Pro app ≈ £10/year",
                url: "https://kurviger.de",
                strengths:
                    "The free web planner is arguably the best free tool for building a curvy multi-stop route. Clean GPX import/export. Community-scored twisty ratings. Open enough that riders trust it.",
                weaknesses:
                    "The UI is spartan. No polished social layer — group sharing is DIY through GPX files. Voice guidance is basic.",
                bestFor:
                    "Planners who build the route carefully at home, load it onto a Garmin or phone, and want the road choice to actually be twisty.",
            },
            {
                name: "komoot",
                tagline: "Cross-discipline — hiking, cycling, and lately motorbiking.",
                cost: "Region packs from ≈ £4 · World pack £30 one-off",
                url: "https://www.komoot.com",
                strengths:
                    "Best web-to-mobile handoff of any app in this list. The planner is opinionated but usually right. Region packs are one-time purchases you own forever. Groups feature is useful for a small crew.",
                weaknesses:
                    "The motorbike routing profile is newer than the cycling one and sometimes misses a good road. Some UI corners still feel cycle-first.",
                bestFor:
                    "Multi-day tours that mix surfaces (some gravel connectors), and for riders who arrive here from cycling.",
            },
        ],
    },
    {
        id: "general",
        heading: "General-purpose maps",
        eyebrow: "The mainstream defaults",
        intro:
            "The apps everyone already has installed. They're not built for riders, but they're free, everywhere, and good enough for the airport hop, the city hotel, or the day you just want to be somewhere by 5pm.",
        tools: [
            {
                name: "Google Maps",
                tagline: "Everyone's default.",
                cost: "Free",
                url: "https://maps.google.com",
                strengths:
                    "Directions almost anywhere on Earth. Voice guidance is best-in-class. Live traffic. Offline maps by area. The 'share ETA' feature is genuinely useful for a group.",
                weaknesses:
                    "Motorbikes are treated as cars, so no 'no motorcycles' sign awareness and no winding-road bias. Zero interest in the fun route.",
                bestFor:
                    "Point-to-point when the road doesn't matter, or navigating an unfamiliar city.",
            },
            {
                name: "Apple Maps",
                tagline: "The iPhone-native option, now much better than it was.",
                cost: "Free",
                strengths:
                    "Very readable turn cards on CarPlay and phone. Look-Around imagery for tricky junctions. On iPhone 15 and later a 'Motorcycle' transport type is available in most EU countries. Genuinely competitive with Google on lookups now.",
                weaknesses:
                    "No twisty-road bias. Rural France routing still occasionally does odd things. Missing detail in the far Alps compared with Google.",
                bestFor:
                    "iPhone-committed riders who want the OS-native experience without another app to open.",
            },
            {
                name: "Waze",
                tagline: "Traffic-first, community-reported hazards.",
                cost: "Free",
                url: "https://waze.com",
                strengths:
                    "Best real-time traffic of any consumer app. Speed-camera and hazard alerts crowd-sourced. Reroutes aggressively around jams.",
                weaknesses:
                    "No offline. Battery hog. Doesn't consider road quality — it'll send you down a farm track to save two minutes.",
                bestFor: "The daily commute. Not touring.",
            },
            {
                name: "HERE WeGo",
                tagline: "The one everyone forgets exists.",
                cost: "Free",
                url: "https://wego.here.com",
                strengths:
                    "Best free offline maps in the business. Country-sized downloads that survive an entire Alps trip on one card. Simple UI.",
                weaknesses:
                    "Feature-light on the planning side. No winding-road bias. No community layer.",
                bestFor:
                    "Backup nav for offline situations, or trips where data roaming will cost you.",
            },
        ],
    },
    {
        id: "gps",
        heading: "Dedicated GPS units",
        eyebrow: "Strap it to the bars",
        intro:
            "Expensive, single-purpose, but genuinely better in bad weather with cold fingers and a sun-drenched screen. A phone breaks; a Zumo mostly doesn't. Three units still worth buying in 2026.",
        tools: [
            {
                name: "Garmin Zumo XT2",
                tagline: "The de-facto motorbike sat-nav.",
                cost: "≈ £500 new · used XT ≈ £300",
                url: "https://www.garmin.com/en-GB/p/1084485",
                strengths:
                    "Glove-friendly capacitive screen that works in the rain. Bright enough for direct sunlight. Waterproof, tank-tough. BaseCamp and Garmin Explore let you plan on a desktop and sync over Bluetooth. Excellent GPX import — drop a file, ride the route.",
                weaknesses:
                    "Really expensive. BaseCamp is a dated piece of software. The mount and cable setup adds another point of failure to the cockpit.",
                bestFor:
                    "Serious tourers on multi-week trips, or anyone who wants their phone to stay in a pocket.",
            },
            {
                name: "TomTom Rider 550 / 500",
                tagline: "The main Zumo alternative.",
                cost: "≈ £350–500",
                url: "https://www.tomtom.com/products/motorcycle-sat-nav/",
                strengths:
                    "Winding-roads mode is genuinely good — a slider that biases toward corners without going full Calimoto. Bluetooth headset pairing is straightforward. Cheaper than a comparable Zumo. Traffic subscription included for the lifetime of the unit.",
                weaknesses:
                    "Companion app connectivity has been shaky historically. Sharing routes between two Riders is clunkier than it should be. Software updates less frequent than Garmin's.",
                bestFor: "Riders who want dedicated hardware without the Garmin price tag.",
            },
            {
                name: "BMW Navigator VI / VII",
                tagline: "The OEM route if you ride a BMW — a Zumo, rebadged.",
                cost: "≈ £500–900",
                url: "https://www.bmw-motorrad.com/",
                strengths:
                    "Deepest integration on any motorbike — the multi-controller wheel scrolls the map, turn indicators talk to the routing, the cradle is engineered by BMW rather than clamped on. Physically bulletproof. Familiar interface if you've used a Garmin Zumo (it is one, rebadged).",
                weaknesses:
                    "Very expensive for what is fundamentally a Zumo. Cradle is bike-specific — swap to a Ducati and it's a paperweight. Newer BMWs push you toward the built-in TFT and the Motorrad Connected app instead.",
                bestFor: "BMW GS / RT / K owners who want the OEM-integrated hardware experience.",
            },
        ],
    },
    {
        id: "mirror",
        heading: "Phone-mirror screens",
        eyebrow: "Your phone, bigger screen",
        intro:
            "Bar-mounted touchscreens that display CarPlay or Android Auto wirelessly from your phone — so you navigate with whatever app you already use, but on a waterproof, sunlight-readable, glove-friendly display.",
        tools: [
            {
                name: "Carpuride W702 / W902",
                tagline: "The mass-market wireless CarPlay screen.",
                cost: "≈ £280–450",
                url: "https://www.carpuride.com",
                strengths:
                    "You navigate with whatever app CarPlay or Android Auto supports — Google Maps, Apple Maps, Waze, Sygic, komoot, and more. Big 7in or 9in screen. Waterproof. No new software to learn. Wireless pairing from a jacket pocket phone works reliably.",
                weaknesses:
                    "Your phone is doing all the work — battery, GPS, data. Chinese-brand firmware updates arrive rarely. Sunlight-readability is good but not Zumo-good.",
                bestFor:
                    "Riders committed to a phone app who want a bigger, tougher screen than their phone can offer on its own.",
            },
            {
                name: "Chigee AIO-5 Lite / Play · AIO-6 LTE",
                tagline: "The category's most polished ecosystem.",
                cost: "≈ £400–700 (AIO-6 LTE ≈ £550–700)",
                url: "https://www.chigee.com",
                strengths:
                    "CarPlay/Android Auto mirroring plus a genuinely useful ecosystem — front dashcam, optional tyre-pressure monitoring, blind-spot radar on the top model. Bright screen. Companion app is better-thought-out than the pure-mirror rivals. The newer AIO-6 LTE — the one I ride with — is more refined than the Carpuride, with more expansion options and physical buttons on the top for quick functions, though it's quite a bit more expensive.",
                weaknesses:
                    "Kitted-out prices approach a Zumo XT2. Software is still a Chinese-brand experience with the occasional English-translation quirk.",
                bestFor:
                    "Tourers who want a middle path between a dumb screen and a full Garmin experience — nav plus dashcam plus TPMS in one shell.",
            },
        ],
    },
];

function MotorbikeNavigationGuide() {
    usePageMeta({
        title:
            "Motorbike navigation tools — what to actually use in 2026 | Planamoto",
        description:
            "The honest map of what to plan a motorbike tour with — Calimoto, Kurviger, komoot, Google Maps, Apple Maps, Garmin Zumo XT2, TomTom Rider, Carpuride, Chigee AIO, BMW Navigator — with the trade-offs I've hit on tour.",
        url: "https://www.planamoto.com/guides/motorbike-navigation-tools",
    });

    return (
        <GuideArticle
            eyebrow="Guide"
            title="Motorbike navigation tools — what to actually use in 2026"
            lede="Plan at home, turn-by-turn on the road, share the plan across the group — three jobs of a routing tool. Most tools do one or two well. Almost none do all three."
            heroImage={{
                src: META.image!.src,
                alt: META.image!.alt ?? "Carpuride CarPlay unit mounted above a motorbike's TFT dashboard",
            }}
            beforeSections={
                <>
                    <GuideStackLead
                        authorNote="Spencer · after a lot of tours getting this wrong"
                        heading="What I actually run"
                        intro="No single app does all three jobs cleanly, so like most serious tourers I run a small stack — one tool per job, plus a paper backstop. The full field guide is below; this is the version I trust on a two-week trip."
                        columns={[
                            {
                                label: "01 · Plan",
                                title: "Planamoto + Google Maps + Calimoto",
                                body: "Planamoto holds the trip itself — the days, overnights and the plan the group shares. Google Maps roughs out the line and the distances; I switch to Calimoto when I want to drag the route onto one specific back road.",
                            },
                            {
                                label: "02 · Navigate",
                                title: "Chigee AIO-6 LTE",
                                body: "My unit is the Chigee AIO-6 LTE — more refined than the Carpuride, though quite a bit more expensive, with more expansion options and physical buttons on the top for quick functions.",
                            },
                            {
                                label: "03 · Share",
                                title: "Planamoto + agreed regroups",
                                body: "The plan lives in Planamoto, so everyone has the same route, overnights and numbers before we leave. A café agreed 40 minutes in. Units always drift; regroups don't.",
                            },
                        ]}
                        backstop="a printed Planamoto sheet in the tank bag — route, overnight address, everyone's number. Batteries fail; paper doesn't."
                    />
                    <div className="gt-guide-transition">
                        <div className="gt-eyebrow gt-guide-transition__eyebrow">
                            The full field
                        </div>
                        <h2 className="gt-guide-transition__heading">
                            That's my stack. Here's everything else, honestly.
                        </h2>
                        <p className="gt-guide-transition__intro">
                            Every tool worth considering, grouped by what it's for — with
                            the trade-offs I've hit on tour, not the marketing copy.
                        </p>
                    </div>
                </>
            }
            articleSchema={{
                slug: META.slug,
                datePublished: META.datePublished,
                dateModified: META.dateModified,
                image: META.image?.src,
                description: META.summary,
            }}
        >
            <GuideThreeJobs
                heading="The three jobs — and why no one tool nails all three"
                jobs={[
                    {
                        title: "Plan",
                        accent: "#c1654a",
                        body: "Building the day's route on a big screen — elevation, corners, accommodation at a sensible dinner distance. A good web planner saves hours.",
                    },
                    {
                        title: "Navigate",
                        accent: "#0f4c5c",
                        body: "Turn-by-turn in the helmet, voice over the wind, screen readable in sun, sensible rerouting when someone misses a turn.",
                    },
                    {
                        title: "Share",
                        accent: "#5b8a72",
                        body: "Getting the plan into everyone's phones and units before you leave. Everyone underrates this and pays for it at the fuel stop.",
                    },
                ]}
            />
            {CATEGORIES.map((cat, i) => {
                const accent = CATEGORY_ACCENT[cat.id];
                return (
                    <GuideSection
                        key={cat.id}
                        index={i + 1}
                        heading={cat.heading}
                        intro={cat.intro}
                        tools={cat.tools}
                        accent={accent}
                        chipLabel={cat.eyebrow}
                    />
                );
            })}

            <GuideSection
                index={CATEGORIES.length + 1}
                heading="Sharing the plan across the group — honestly"
                intro="Group navigation is not a solved problem. Hand five riders the same GPX and five units pick five different lines between the waypoints — the group splits at 60mph and reunites 40 minutes later, tempers frayed. A few things actually work, in order of how much control you keep:"
                highlight={{ glyph: "★", background: "var(--gt-accent)", color: "#3a2a0a" }}
                items={[
                    {
                        title: "Mark the junctions, not the roads",
                        body: "Scatter a waypoint at every decision point — every T-junction, fork and fuel stop. Ten for a 100-mile day isn't too many. Less freedom to invent, and the units converge.",
                    },
                    {
                        title: "Agree a first stop, then ride your own ride",
                        body: "Pick a café 30–60 min in. Everyone rides their own line and pace to get there, regroup, agree the next. How experienced tourers actually operate.",
                    },
                    {
                        title: "Live-position layer",
                        body: "Cardo Group Chat, komoot Groups or Ride with GPS Events show where each rider is. Doesn't fix different turn instructions, but you know who went wrong and where.",
                    },
                    {
                        title: "The paper backstop",
                        body: "One sheet per day in the tank bag — route, overnight address, everyone's number. Planamoto's printable export is built for this. Batteries fail; paper doesn't.",
                    },
                ]}
            />

            <GuideSection
                index={CATEGORIES.length + 2}
                columns={[
                    {
                        heading: "Offline, battery & boring truths",
                        items: [
                            {
                                marker: "✓",
                                title: "Load offline maps before the border",
                                body: "Roaming is fine in the EU but slow in the high Alps. Google, Apple, HERE, komoot packs and every GPS unit work without data.",
                            },
                            {
                                marker: "✓",
                                title: "Fit a bike-side charger",
                                body: "Max brightness at noon drains a phone 50% in two hours. Hotel-only charging fails on any day over 200 miles.",
                            },
                            {
                                marker: "✓",
                                title: "Mount it properly",
                                body: "If the phone is your only nav, budget for a Quad Lock or SP Connect with a silicon sleeve — not a stretchy stem strap.",
                            },
                        ],
                    },
                    {
                        heading: "File formats",
                        items: [
                            {
                                marker: "GPX",
                                title: "The universal one",
                                body: "Every app here reads and writes it. If you learn one format, learn this — plain XML, human-readable.",
                            },
                            {
                                marker: "TCX",
                                title: "Garmin's older format",
                                body: "Still around on some devices; rarely needed for touring. The fallback if a Garmin refuses your GPX.",
                            },
                            {
                                marker: "KML",
                                title: "Google Earth's format",
                                body: "Turns up in tourism-office downloads. Convert to GPX with a free tool before importing elsewhere.",
                            },
                        ],
                    },
                ]}
            />

            <GuideCtaBand
                heading="We handle the plan. Your nav handles the turns."
                body="Stops, overnight address, expenses, checklists and the group live in Planamoto. Each day gives you a route link into Google Maps today — per-day GPX export to Calimoto, komoot or a Zumo is on the roadmap."
            />

            <RelatedGuides
                slugs={[
                    "cycle-navigation-tools",
                    "french-toll-roads",
                    "packing-europe",
                    "fuel-prices",
                    "power-plugs",
                ]}
            />

            <CtaCard
                title="Plan the day, hand off to your nav"
                body="Once the route's decided, the plan around it — stops, accommodation, expenses across currencies, group checklists — lives in Planamoto. Browse a curated tour to see how a day looks, or start your own."
                primary={{ label: "Browse tours", to: "/explore" }}
            />
        </GuideArticle>
    );
}

export default MotorbikeNavigationGuide;
